import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchApprovedProfiles } from "../api";
import { resolveImageUrl } from "../utils/imageUrl";
import "../assets/css/LatestProfilesTicker.css";

const MAX_PROFILES = 16;
const PLACEHOLDER = `${process.env.PUBLIC_URL}/images/default.png`;

const formatAge = (p) => {
  if (p.Age) return `${p.Age} yrs`;
  if (!p.DateOfBirth) return null;
  const diff = Date.now() - new Date(p.DateOfBirth).getTime();
  const age = new Date(diff).getUTCFullYear() - 1970;
  return Number.isFinite(age) && age > 0 ? `${age} yrs` : null;
};

const profileName = (p) =>
  `${p.firstname || ""} ${p.lastname || ""}`.trim() || "Member";

const profileThumb = (p) =>
  resolveImageUrl(p.ProfileImageURL || p.ProfilePhoto) || PLACEHOLDER;

function buildMarqueeItems(list) {
  if (!list.length) return [];
  let row = [...list];
  while (row.length < 5) row = [...row, ...list];
  return [...row, ...row];
}

function ProfileCard({ profile, href, style }) {
  const name = profileName(profile);
  const age = formatAge(profile);
  const city = profile.ContactCity || profile.CityName;

  return (
    <Link
      to={href(profile.ProfileID)}
      className="dw-latest-card"
      style={style}
    >
      <div className="dw-latest-card__ring">
        <div className="dw-latest-card__img-wrap">
          <img
            src={profileThumb(profile)}
            alt={name}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = PLACEHOLDER;
            }}
          />
          <span className="dw-latest-card__shine" aria-hidden="true" />
        </div>
      </div>

      <div className="dw-latest-card__body">
        <span className="dw-latest-card__badge">
          <i className="fa fa-shield" aria-hidden="true" /> Verified
        </span>
        <h3>{name}</h3>
        <div className="dw-latest-card__tags">
          {age && (
            <span>
              <i className="fa fa-birthday-cake" aria-hidden="true" /> {age}
            </span>
          )}
          {city && (
            <span>
              <i className="fa fa-map-marker" aria-hidden="true" /> {city}
            </span>
          )}
          {profile.GotraName && <span>{profile.GotraName}</span>}
        </div>
        <span className="dw-latest-card__action">
          View profile <i className="fa fa-arrow-right" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}

function MarqueeRow({ items, direction, paused, profileHref, duration }) {
  return (
    <div
      className={`dw-latest-marquee dw-latest-marquee--${direction}${
        paused ? " is-paused" : ""
      }`}
    >
      <div
        className={`dw-latest-marquee__track dw-latest-marquee__track--${direction}`}
        style={{ "--marquee-duration": `${duration}s` }}
      >
        {items.map((p, i) => (
          <ProfileCard
            key={`${p.ProfileID}-${direction}-${i}`}
            profile={p}
            href={profileHref}
            style={{ "--card-i": i % 8 }}
          />
        ))}
      </div>
    </div>
  );
}

const LatestProfilesTicker = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paused, setPaused] = useState(false);

  const isLoggedIn = useMemo(() => {
    const id = localStorage.getItem("profileID");
    return !!id && id !== "undefined" && id !== "null";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchApprovedProfiles();
        if (!cancelled) {
          setProfiles((result.data || []).slice(0, MAX_PROFILES));
        }
      } catch (err) {
        console.warn("Latest profiles:", err?.message || err);
        if (!cancelled) setProfiles([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rowA = useMemo(
    () => buildMarqueeItems(profiles.filter((_, i) => i % 2 === 0)),
    [profiles],
  );
  const rowB = useMemo(
    () => buildMarqueeItems(profiles.filter((_, i) => i % 2 === 1)),
    [profiles],
  );

  const duration = Math.max(32, Math.max(rowA.length, rowB.length) * 3.2);

  const profileHref = (id) =>
    isLoggedIn
      ? `/user-profile/${id}`
      : `/login?next=${encodeURIComponent(`/user-profile/${id}`)}`;

  if (!loading && profiles.length === 0) return null;

  return (
    <section className="dw-latest-profiles" aria-label="Latest profiles">
      <div className="dw-latest-profiles__bg" aria-hidden="true">
        <span className="dw-latest-blob dw-latest-blob--1" />
        <span className="dw-latest-blob dw-latest-blob--2" />
        <span className="dw-latest-blob dw-latest-blob--3" />
      </div>

      <div className="container dw-latest-profiles__head">
        <div className="dw-latest-head">
          <span className="dw-latest-live">
            <span className="dw-latest-live__dot" />
            Live now
          </span>
          <h2 className="dw-latest-head__title">Latest Profiles</h2>
          <p className="dw-latest-head__sub">
            Hand-picked verified members — join free &amp; connect today.
          </p>
          {!loading && (
            <p className="dw-latest-head__count">
              <strong>{profiles.length}+</strong> active profiles showing
            </p>
          )}
        </div>
      </div>

      <div
        className={`dw-latest-marquee-stack${loading ? " is-loading" : ""}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <div className="dw-latest-marquee__fade dw-latest-marquee__fade--left" />
        <div className="dw-latest-marquee__fade dw-latest-marquee__fade--right" />

        {loading ? (
          <div className="dw-latest-marquee dw-latest-marquee--fwd">
            <div className="dw-latest-marquee__track dw-latest-marquee__track--skeleton">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div
                  key={n}
                  className="dw-latest-card dw-latest-card--skeleton"
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <MarqueeRow
              items={rowA}
              direction="fwd"
              paused={paused}
              profileHref={profileHref}
              duration={duration}
            />
            <MarqueeRow
              items={rowB.length ? rowB : rowA}
              direction="rev"
              paused={paused}
              profileHref={profileHref}
              duration={duration * 1.15}
            />
          </>
        )}
      </div>

      <div className="container dw-latest-profiles__foot">
        <Link to="/all-profiles" className="dw-latest-profiles__cta">
          <span>Explore all profiles</span>
          <i className="fa fa-arrow-right" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
};

export default LatestProfilesTicker;
